import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { newServicesAction, hairdresserActions } from '../../../../../_actions'
import { productAction } from '../../../../../_actions/product.action'
import { reviewService } from '../../../../../_services/review.service'
import { giftCardService } from '../../../../../_services/giftCard.service'
import { THEMES } from '../../../../gift-cards-v2/data'
import { loadDesignLibrary, designsForVoucher } from './voucherDesigns'
import { getShareContext, buildShareUrl } from '../../../../shared/shareLinks'

// ─── Content types offered in the panel ───
export const CONTENT_TYPES = [
  { type: 'service', label: 'Service', desc: 'Feature a treatment with its price', hasStyles: true },
  { type: 'product', label: 'Product', desc: 'Sell retail, linked to your shop', hasStyles: true },
  { type: 'voucher', label: 'Gift Voucher', desc: 'Sell vouchers from your gift card shop', hasStyles: true },
  { type: 'review', label: 'Review', desc: 'Real words from real clients', hasStyles: false },
  { type: 'team', label: 'Team Member', desc: 'Introduce the people behind the work', hasStyles: false },
]

export const contentTypeMeta = (type) => CONTENT_TYPES.find(t => t.type === type) || null

// Panel title for each step of the flow
export const contentFlowTitle = (flow) => {
  if (!flow) return 'Content'
  const meta = contentTypeMeta(flow.type)
  const label = meta ? meta.label.toLowerCase() : 'item'
  if (flow.step === 'style') return 'Choose a style'
  if (flow.mode === 'swap') return `Swap ${label}`
  return `Choose a ${label}`
}

// ─── Block style options per content type ───
const STYLE_OPTIONS = {
  service: [
    { id: 'card', name: 'Card', desc: 'Image beside the details' },
    { id: 'stacked', name: 'Stacked', desc: 'Image above the details' },
    { id: 'minimal', name: 'Minimal', desc: 'A clean menu row' },
  ],
  product: [
    { id: 'card', name: 'Card', desc: 'Image beside the details' },
    { id: 'stacked', name: 'Stacked', desc: 'Image above the details' },
    { id: 'minimal', name: 'Minimal', desc: 'A clean menu row' },
  ],
  voucher: [
    { id: 'showcase', name: 'Showcase', desc: 'The full card with a buy button' },
    { id: 'split', name: 'Split', desc: 'Card beside the details' },
    { id: 'minimal', name: 'Minimal', desc: 'A clean banner row' },
  ],
}
const styleOptionsFor = (type) => STYLE_OPTIONS[type] || STYLE_OPTIONS.service

// Mini visual previews for the style tiles
const StylePreview = ({ id, type }) => {
  const img = '#C9A9A6'
  const line = '#D4C5B2'
  const ink = '#2C3E50'
  if (type === 'voucher') {
    const card = '#E8B4C8'
    if (id === 'showcase') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 8, height: '100%' }}>
          <div style={{ width: '62%', height: 26, background: card, borderRadius: 5 }} />
          <div style={{ height: 3, width: '45%', background: line, borderRadius: 1 }} />
          <div style={{ height: 8, width: '40%', background: ink, borderRadius: 100 }} />
        </div>
      )
    }
    if (id === 'split') {
      return (
        <div style={{ display: 'flex', gap: 6, padding: 8, height: '100%', alignItems: 'center' }}>
          <div style={{ width: 34, height: 24, background: card, borderRadius: 4, flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ height: 4, width: '75%', background: ink, borderRadius: 2 }} />
            <div style={{ height: 3, width: '55%', background: line, borderRadius: 1 }} />
            <div style={{ height: 7, width: '50%', background: ink, borderRadius: 100, marginTop: 2 }} />
          </div>
        </div>
      )
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '0 10px', height: '100%' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ height: 4, width: '70%', background: ink, borderRadius: 2 }} />
          <div style={{ height: 3, width: '50%', background: line, borderRadius: 1 }} />
        </div>
        <div style={{ height: 9, width: 30, background: ink, borderRadius: 100, flexShrink: 0 }} />
      </div>
    )
  }
  if (id === 'card') {
    return (
      <div style={{ display: 'flex', gap: 5, padding: 8, height: '100%', alignItems: 'center' }}>
        <div style={{ width: 26, alignSelf: 'stretch', background: img, borderRadius: 4 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ height: 5, width: '75%', background: ink, borderRadius: 2 }} />
          <div style={{ height: 3, width: '95%', background: line, borderRadius: 1 }} />
          <div style={{ height: 5, width: '35%', background: ink, borderRadius: 2, marginTop: 2 }} />
        </div>
      </div>
    )
  }
  if (id === 'stacked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 8, height: '100%' }}>
        <div style={{ flex: 1, background: img, borderRadius: 4 }} />
        <div style={{ height: 4, width: '65%', background: ink, borderRadius: 2 }} />
        <div style={{ height: 3, width: '85%', background: line, borderRadius: 1 }} />
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 10px', height: '100%', justifyContent: 'center' }}>
      {[1, 2].map(i => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 5, borderBottom: '1px solid #ECECF0' }}>
          <div style={{ height: 4, width: '55%', background: ink, borderRadius: 2 }} />
          <div style={{ height: 4, width: '15%', background: ink, borderRadius: 2 }} />
        </div>
      ))}
    </div>
  )
}

// Icons for the type rows (1.7 stroke to match the rail)
const tIcon = { width: 19, height: 19, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
const TYPE_ICONS = {
  service: <svg {...tIcon}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  product: <svg {...tIcon}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
  voucher: <svg {...tIcon}><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>,
  review: <svg {...tIcon}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  team: <svg {...tIcon}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
}

const Stars = ({ rating }) => (
  <span style={{ display: 'inline-flex', gap: 1 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <svg key={i} width="11" height="11" viewBox="0 0 20 20" fill={i <= rating ? '#f59e0b' : '#e5e7eb'} aria-hidden>
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
      </svg>
    ))}
  </span>
)

// Friendly empty states per type
const EMPTY_COPY = {
  service: ['No services yet', 'Add services to your menu and they will appear here.'],
  product: ['No products yet', 'Add retail products and they will appear here.'],
  voucher: ['No live vouchers', 'Publish a voucher with online sales turned on and it will appear here.'],
  review: ['No reviews yet', 'Reviews appear here once clients leave them.'],
  team: ['No team members', 'Add staff to your salon and they will appear here.'],
}

// ─── Data loading: mirrors the proven call sites used by working screens ───
export const loadItems = async (type, dispatch, user) => {
  const hairdresser = user?.userInfo?.hairdresser || {}
  const currency = hairdresser.currencySymbol || '£'
  const { salonSlug } = getShareContext(hairdresser)

  if (type === 'service') {
    const response = await dispatch(newServicesAction.listServicesData({}))
    const categories = response?.listServices || []
    const all = []
    categories.forEach(category => {
      ;(category.serviceList || []).forEach(service => {
        if (!service.isDeactivated) {
          all.push({
            id: service.commonServiceId,
            name: service.serviceName,
            description: service.serviceDescription || '',
            price: service.servicePrice || 0,
            duration: service.serviceTime ? `${service.serviceTime} min` : '',
            image: service.serviceImage || null,
            category: category.categoryName,
            currency,
          })
        }
      })
    })
    return all
  }

  if (type === 'product') {
    const response = await dispatch(productAction.getProductsList({ salonId: localStorage.getItem('salonId') }))
    const list = response?.data?.responseData?.result || []
    const firstImage = (p) => {
      const arr = p.images || p.photos || []
      const urls = (Array.isArray(arr) ? arr : [])
        .map(im => (typeof im === 'string' ? im : (im && (im.url || im.src || im.image))))
        .filter(Boolean)
      return urls[0] || p.image || p.productImage || null
    }
    return list.map(p => ({
      id: p._id,
      name: p.productName || p.name || 'Product',
      description: p.description || '',
      price: (Number(p.retailPrice) || 0).toFixed(2),
      currency,
      image: firstImage(p),
      url: buildShareUrl({ type: 'product', id: p._id, salonSlug }),
      sellOnline: p.sellOnline !== false,
    }))
  }

  if (type === 'voucher') {
    const [vouchers, designLib] = await Promise.all([
      giftCardService.listVouchers(),
      loadDesignLibrary(),
    ])
    const salonName = hairdresser.salon?.salonName || hairdresser.salon?.name || 'Your Salon'
    return (vouchers || [])
      .filter(v => v.status === 'active' && v.online)
      .map(v => {
        const theme = THEMES.find(t => t.id === v.theme) || THEMES.find(t => t.id === 'rose') || THEMES[0]
        const fromPrice = (v.denominations && v.denominations.length) ? Math.min(...v.denominations) : (v.value || 0)
        // The voucher's cover design (or its first allocated design) so the
        // inserted block immediately shows the real card face
        const allocated = designsForVoucher(designLib, v.designIds || [], v.coverDesignId)
        return {
          id: v.id,
          name: v.name || 'Gift Voucher',
          fromPrice: String(fromPrice),
          value: v.value || fromPrice || 50,
          promo: v.promo,
          currency,
          expiry: v.expiry || '',
          url: buildShareUrl({ type: 'voucher', id: v.id, salonSlug }),
          template: v.template || 'signature',
          theme: theme.id,
          salonName,
          // Design allocation, carried into the block for its design grid
          designIds: v.designIds || [],
          coverDesignId: v.coverDesignId || null,
          coverDesign: allocated[0] || null,
          // Swatch colours for the picker row only
          bg: theme.bg,
          accent: theme.accent,
        }
      })
  }

  if (type === 'review') {
    // Same endpoint the salon profile uses; requires salonId or the API returns nothing
    const resp = await reviewService.getSalonReviews(localStorage.getItem('salonId'), 0, 50)
    const reviews = resp?.responseData?.reviews || []
    return reviews
      .map(r => {
        const isGoogle = r.source === 'google'
        const author = isGoogle
          ? (r.authorName || 'Google review')
          : (r.userId?.name
              || [r.userId?.first_name, r.userId?.last_name].filter(Boolean).join(' ')
              || r.clientName
              || 'Anonymous')
        const firstService = (r.services || [])[0]
        return {
          id: r._id,
          text: r.reviewText || r.comment || '',
          author,
          service: typeof firstService === 'string' ? firstService : (firstService?.serviceName || ''),
          rating: r.rating || 5,
          date: r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '',
        }
      })
      .filter(r => r.text)
  }

  if (type === 'team') {
    // hairdresserStaffRota is the maintained staff list action (the old
    // hairdresserStaff action hits a dead v1 endpoint and returns nothing)
    const response = await dispatch(hairdresserActions.hairdresserStaffRota({
      searchKey: '', hairdresserId: localStorage.getItem('userId'), page: 1, listType: 1, isGetRota: 1,
    }))
    const rota = response?.data?.data?.rota || []
    return rota
      .filter(h => h.name && h.name !== 'Unknown')
      .map(h => ({
        id: h._id,
        name: h.name,
        title: h.hairdresserInfo?.jobTitle || 'Stylist',
        bio: 'Experienced professional',
        image: h.userImage?.circleImage || null,
      }))
  }

  return []
}

const matches = (type, item, q) => {
  if (!q) return true
  const s = q.toLowerCase()
  const fields = {
    service: [item.name, item.description, item.category],
    product: [item.name, item.description],
    voucher: [item.name],
    review: [item.text, item.author, item.service],
    team: [item.name, item.title],
  }[type] || []
  return fields.some(f => f && f.toLowerCase().includes(s))
}

/**
 * In-panel content flow: type rows -> (style) -> item list.
 * flow: null | { type, step: 'style' | 'pick', style, mode: 'insert' | 'swap', targetId }
 */
const ContentPanelComponent = ({ flow, onStartType, onPickStyle, onPickItem, dispatch, user }) => {
  const [search, setSearch] = useState('')
  const [items, setItems] = useState(null) // null = loading
  const [loadError, setLoadError] = useState(false)

  const pickType = flow && flow.step === 'pick' ? flow.type : null

  useEffect(() => {
    setSearch('')
    if (!pickType) { setItems(null); return undefined }
    let alive = true
    setItems(null)
    setLoadError(false)
    loadItems(pickType, dispatch, user)
      .then(list => { if (alive) setItems(list) })
      .catch(err => {
        console.error('Content load failed:', err)
        if (alive) { setItems([]); setLoadError(true) }
      })
    return () => { alive = false }
  }, [pickType]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step: content type rows ──
  if (!flow) {
    return (
      <div>
        {CONTENT_TYPES.map(t => (
          <button key={t.type} type="button" className="eb-insert-row" onClick={() => onStartType(t)}>
            <span className="eb-insert-icon">{TYPE_ICONS[t.type]}</span>
            <span className="eb-insert-text">
              <span className="eb-insert-title">{t.label}</span>
              <span className="eb-insert-desc">{t.desc}</span>
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 18l6-6-6-6"/></svg>
          </button>
        ))}
      </div>
    )
  }

  // ── Step: pick a box style ──
  if (flow.step === 'style') {
    return (
      <div>
        <p className="eb-insert-section-label">How should it look?</p>
        <div className="eb-style-list">
          {styleOptionsFor(flow.type).map(s => (
            <button key={s.id} type="button" className="eb-style-tile" onClick={() => onPickStyle(s.id)}>
              <div className="eb-style-thumb"><StylePreview id={s.id} type={flow.type} /></div>
              <div className="eb-style-text">
                <span className="eb-insert-title">{s.name}</span>
                <span className="eb-insert-desc">{s.desc}</span>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 18l6-6-6-6"/></svg>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ── Step: pick the item ──
  const loading = items === null
  const filtered = (items || []).filter(i => matches(flow.type, i, search))
  const [emptyTitle, emptyBody] = EMPTY_COPY[flow.type] || ['Nothing here yet', '']

  return (
    <div>
      {(items?.length || 0) > 5 && (
        <input
          className="eb-pick-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
        />
      )}

      {loading && (
        <div>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="eb-pick-row eb-pick-row--skel">
              <div className="eb-skel eb-pick-thumb" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div className="eb-skel" style={{ height: 11, width: '60%', borderRadius: 4 }} />
                <div className="eb-skel" style={{ height: 9, width: '40%', borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="eb-pick-empty">
          <p className="eb-pick-empty-title">{search ? 'No matches' : (loadError ? 'Could not load' : emptyTitle)}</p>
          <p className="eb-pick-empty-body">{search ? 'Try a different search.' : (loadError ? 'Check your connection and reopen this panel.' : emptyBody)}</p>
        </div>
      )}

      {!loading && filtered.map(item => {
        if (flow.type === 'review') {
          return (
            <button key={item.id} type="button" className="eb-pick-row eb-pick-row--tall" onClick={() => onPickItem(item)}>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                  <Stars rating={item.rating} />
                  {item.service && <span className="eb-pick-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.service}</span>}
                </div>
                <p className="eb-pick-quote">"{item.text}"</p>
                <span className="eb-pick-sub">{item.author}{item.date ? ` · ${item.date}` : ''}</span>
              </div>
            </button>
          )
        }
        if (flow.type === 'voucher') {
          return (
            <button key={item.id} type="button" className="eb-pick-row" onClick={() => onPickItem(item)}>
              <span className="eb-pick-thumb" style={{ backgroundColor: item.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: 'Georgia, serif', fontSize: 16, color: item.accent }}>{item.currency}</span>
              </span>
              <span className="eb-pick-text">
                <span className="eb-pick-title">{item.name}</span>
                <span className="eb-pick-sub">From {item.currency}{item.fromPrice}{item.expiry && item.expiry !== 'Never' ? ` · ${item.expiry}` : ''}</span>
              </span>
            </button>
          )
        }
        if (flow.type === 'team') {
          return (
            <button key={item.id} type="button" className="eb-pick-row" onClick={() => onPickItem(item)}>
              <span className="eb-pick-thumb eb-pick-thumb--round" style={item.image ? { backgroundImage: `url(${item.image})` } : {}}>
                {!item.image && <span className="eb-pick-initial">{item.name.charAt(0)}</span>}
              </span>
              <span className="eb-pick-text">
                <span className="eb-pick-title">{item.name}</span>
                <span className="eb-pick-sub">{item.title}</span>
              </span>
            </button>
          )
        }
        // service / product
        return (
          <button key={item.id} type="button" className="eb-pick-row" onClick={() => onPickItem(item)}>
            <span className="eb-pick-thumb" style={item.image ? { backgroundImage: `url(${item.image})` } : {}}>
              {!item.image && <span className="eb-pick-initial">{item.name.charAt(0)}</span>}
            </span>
            <span className="eb-pick-text">
              <span className="eb-pick-title">{item.name}</span>
              <span className="eb-pick-sub">
                {item.currency}{item.price}
                {flow.type === 'service' && item.duration ? ` · ${item.duration}` : ''}
                {flow.type === 'product' && !item.sellOnline ? ' · not sold online' : ''}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

const mapStateToProps = (state) => ({ user: state.user })
export const ContentPanel = connect(mapStateToProps)(ContentPanelComponent)
